import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSpinner,
  Icon,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function GroupsCommandList<T extends boolean = false>({
  selected = [],
  multiselect,
  ...props
}: BaseCommandListProps<T, string>) {
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Groups)

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return data
    const normalizedQuery = query.toLowerCase()
    return data?.filter(
      (group) =>
        !props.excludeIds?.includes(group.id) &&
        group.name.toLowerCase().includes(normalizedQuery),
    )
  }, [data, query, isSearching, props.excludeIds])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      props.onSelect(newSelection)
    },
    [props.onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        props.onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        props.onSelect([id])
      }
    },
    [multiselect, selected, props.onSelect],
  )

  const handleClear = useCallback(() => {
    props.onSelect([])
  }, [props.onSelect])

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      <CommandInput
        placeholder={props.placeholder || 'Filter...'}
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="py-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={multiselect}
          />
          {props.allowEmpty && (
            <CommandItem onSelect={handleClear} className="gap-2">
              <Icon name="x" />
              {props.allowEmptyLabel || 'Clear'}
            </CommandItem>
          )}
          {visibleItems?.map((group) => (
            <CommandCheckItem
              key={group.id}
              value={group.id}
              keywords={[group.name]}
              selected={selected.includes(group.id)}
              onSelect={() => handleToggleItem(group.id)}
              multiselect={multiselect}
            >
              {group.name}
            </CommandCheckItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
